import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CheckCircle, Shield, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePendingPrescription } from '@/contexts/PendingPrescriptionContext';

interface VerificationConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prescriptionId?: string;
  patientName?: string;
  practitionerName?: string;
  onConfirm: () => Promise<void> | void;
  isLoading?: boolean;
}

const VerificationConfirmationDialog: React.FC<VerificationConfirmationDialogProps> = ({
  open,
  onOpenChange,
  prescriptionId,
  patientName,
  practitionerName,
  onConfirm,
  isLoading = false,
}) => {
  const { refreshCount } = usePendingPrescription();
  
  const handleConfirm = async () => {
    try {
      await onConfirm();
      onOpenChange(false);
      await refreshCount();
    } catch (error) {
      // Error handling is done in the parent component
      console.error(error);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-emerald-500/10">
              <Shield className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-[1.05rem] font-semibold">
                Verify Prescription
              </DialogTitle>
              <DialogDescription className="text-[0.8rem] text-slate-500 mt-0">
                Confirm prescription verification
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-2 space-y-3">
          {patientName && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Patient:</span>
              <span className="font-medium text-slate-900">{patientName}</span>
            </div>
          )}
          {practitionerName && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Practitioner:</span>
              <span className="font-medium text-slate-900">{practitionerName}</span>
            </div>
          )}
          
          <div className="bg-emerald-50 border border-emerald-100 p-3 rounded-lg mt-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-emerald-800">
                <p className="font-medium">This action will:</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5 text-emerald-700">
                  <li>Mark the prescription as verified</li>
                  <li>Enable payment processing</li>
                  <li>Allow the prescription to proceed to pharmacy</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Verifying...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Verify Prescription
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationConfirmationDialog;
